import React, { useState, useEffect } from "react";

import { fetchDataGroupBoards } from "../../services/monday-service";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { Board, Group, Warning } from "@vibe/icons";
import { Search, Tooltip } from "@vibe/core";
import { Fragment } from "react";
import Spinner from "../Spinner";
import ButtonComponent from "../Button";
import useSubitemContext from "../../hooks/context/subitemContext";
import { useList } from "../../hooks/ui";
import { useWorkspaceAndBoards } from "../../hooks/api/useWorkspacesAndBoards";
import logger from "../../lib/logger";
import { useDispatch } from "react-redux";
import { setBoardId, setBoardName } from "../../redux/slices/onboardingTargetBoardNameSlice";
import { setAddItemSuccess } from "../../redux/slices/addItemSuccessSlice";
import { Checkbox } from "@vibe/core";
import { WorkspaceIcon } from "./WorkspaceList";
import SharedColumnsPreview from "./SharedColumnsPreview";

const client_api_key = process.env.REACT_APP_CLIENT_ID;
const redirect_uri = process.env.REACT_APP_REDIRECT_URI;

const WorkspacesAndBoardsList = ({
  searchText,
  addItem,
  itemsLoader = [],
  itemsDisabled = [],
  isDisableAll,
  handleSearch,
  processUpdates,
  setProcessUpdates,
  processSubitems,
  setProcessSubitems,
  onGroupSelect,
}) => {
  const [openWorkspaces, setOpenWorkspaces] = useState({});
  const [openBoards, setOpenBoards] = useState({});
  const [boardGroups, setBoardGroups] = useState({});
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [retry, setRetry] = useState(false);
  const [reauth, setReauth] = useState(0);
  // { workspaceName, board, group } once a group is picked; the list stays mounted state-wise behind it.
  const [preview, setPreview] = useState(null);

  const dispatch = useDispatch();

  const {
    isLoading,
    error,
    workspacesAndBoards,
    mutateWorkspacesAndBoards,
    success,
    isValidating,
  } = useWorkspaceAndBoards();

  const { cleanUpDisabled } = useList();

  const { parentBoard } = useSubitemContext();

  useEffect(() => {
    // Reset retry count when the searchText changes (assuming this should reset retries on new searches)
    if (success) {
      setReauth(0);
    }
  }, [success]);

  useEffect(() => {
    if (isValidating) {
      cleanUpDisabled(workspacesAndBoards);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspacesAndBoards]);

  const handleWorkspaceClick = (workspaceId) => {
    setOpenWorkspaces((prevState) => ({
      ...prevState,
      [workspaceId]: !prevState[workspaceId],
    }));
    setSelectedWorkspace(workspaceId);
  };

  const handleBoardClick = async (workspaceId, boardId) => {
    if (!(openBoards[workspaceId] && openBoards[workspaceId][boardId])) {
      const result = await fetchDataGroupBoards(boardId);

      setBoardGroups((prevGroups) => ({
        ...prevGroups,
        [boardId]: result,
      }));
    }

    setOpenBoards((prevState) => ({
      ...prevState,
      [workspaceId]: {
        ...prevState[workspaceId],
        [boardId]: !(prevState[workspaceId] && prevState[workspaceId][boardId]),
      },
    }));
  };

  const filterByWorkspaceBoards = (workspace) => {
    const isFilteredByWorkspace = workspace.name
      .toLowerCase()
      .includes(searchText.toLowerCase().trim());

    if (isFilteredByWorkspace) {
      return workspace.boards;
    } else {
      return workspace.boards.filter((board) =>
        board.name.toLowerCase().includes(searchText.toLowerCase()),
      );
    }
  };

  const filterByWorkspaceAndBoards = () => {
    return workspacesAndBoards.filter(
      (workspace) =>
        workspace.name.toLowerCase().includes(searchText.toLowerCase()) ||
        workspace?.boards.some((board) =>
          board.name.toLowerCase().includes(searchText.toLowerCase()),
        ),
    );
  };

  const FilteredWorkspacesAndBoards = searchText
    ? filterByWorkspaceAndBoards()
    : workspacesAndBoards;

  useEffect(() => {
    const newOpenWorkspaces = {};
    if (searchText.trim()) {
      // Expand only those workspaces that match the search criteria or contain boards that match.
      workspacesAndBoards.forEach((workspace) => {
        const isMatch =
          workspace.name.toLowerCase().includes(searchText.toLowerCase()) ||
          workspace.boards.some((board) =>
            board.name.toLowerCase().includes(searchText.toLowerCase()),
          );
        newOpenWorkspaces[workspace.id] = isMatch; // Expand if there's a match
      });
    } else {
    }
  }, [searchText, workspacesAndBoards]);

  const retryFetch = async () => {
    try {
      setRetry(true);
      setReauth(reauth + 1);
      await mutateWorkspacesAndBoards();
    } catch (error) {
      logger.error(error);
    } finally {
      setRetry(false);
    }
  };

  if (error) {
    return (
      <div className="flex flex-col gap-2 justify-center items-center">
        {reauth >= 1 ? (
          <div className="flex items-center text-center justify-center max-w-[241px]">
            <span>
              If the error persists, try authorizing again using this{" "}
              <a
                className="hover:underline"
                rel="noreferrer"
                href={`https://auth.monday.com/oauth2/authorize?client_id=${client_api_key}&redirect_uri=${redirect_uri}`}
                target="_blank"
              >
                link
              </a>
            </span>
          </div>
        ) : (
          <>
            <div className="flex justify-center items-center">
              <Warning className="mr-1" />
              <span>An error occurred. Please try again.</span>
            </div>
          </>
        )}

        <div className="flex justify-center">
          <ButtonComponent text="Retry" className="retry-button" onClick={retryFetch} />
        </div>
      </div>
    );
  }

  if (preview) {
    const { workspaceName, board, group } = preview;
    return (
      <SharedColumnsPreview
        boardId={board.id}
        boardName={board.name}
        group={group}
        breadcrumbs={[workspaceName, board.name, group.title]}
        onBack={() => setPreview(null)}
        confirmDisabled={
          board.synced ||
          isDisableAll ||
          itemsDisabled.some((item) => item.endsWith(`-${board.id}`))
        }
        onConfirm={() => {
          onGroupSelect?.();
          dispatch(setBoardName(board?.name));
          dispatch(setAddItemSuccess(true));
          addItem(group.id, board.id);
          setPreview(null);
        }}
      />
    );
  }

  return (
    <>
      {isLoading || retry ? (
        <div className="flex gap-2 items-center mt-2">
          <Spinner /> Getting your workspaces and boards
        </div>
      ) : (
        <div id="add_item_step_2_small">
          <div className="pt-[15px] pb-[12px] bg-[#FFFFFF] sticky top-0 z-[1] border-b border-[#C3C6D4] overflow-x-hidden">
            <Search
              placeholder="Search Workspaces and Boards"
              onChange={(e) => {
                handleSearch(e);
              }}
              size="small"
              style={{
                width: "100%",
              }}
              value={searchText}
            />

            <div className="flex flex-col gap-2 mt-2">
              <Checkbox
                label="Include Subitems"
                checked={processSubitems}
                onChange={(e) => {
                  setProcessSubitems(e.target.checked);
                }}
              />

              <Checkbox
                label="Include Updates"
                checked={processUpdates}
                onChange={(e) => {
                  setProcessUpdates(e.target.checked);
                }}
              />
            </div>
          </div>
          <List
            sx={{
              width: "100%",
              bgcolor: "background.paper",
            }}
            className="!text-xs"
            component="nav"
          >
            {FilteredWorkspacesAndBoards.map((workspace, index) => {
              const filteredBoards = searchText.trim()
                ? filterByWorkspaceBoards(workspace)
                : workspace.boards;

              return (
                <Fragment key={workspace.id}>
                  {/* Workspace List Item */}
                  <ListItemButton
                    sx={{
                      bgcolor: openWorkspaces[workspace.id] ? "#CCE5FF" : "inherit",
                      "&:hover": {
                        bgcolor: selectedWorkspace === workspace.id ? "#E6E9EF" : "inherit",
                      },
                      borderRadius: "4px",
                    }}
                    onClick={() => {
                      handleWorkspaceClick(workspace.id);
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: "unset" }} className="!w-auto mr-2">
                      <WorkspaceIcon
                        workspace={workspace}
                        index={index}
                        containerSize={20}
                        iconSize={14}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primaryTypographyProps={{
                        fontSize: "0.875rem",
                        fontFamily: "Poppins",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      primary={`${workspace.name}`}
                    />
                    {openWorkspaces[workspace.id] ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>

                  {/* Boards and Items (Subitems) */}
                  <Collapse in={openWorkspaces[workspace.id]} timeout={100} unmountOnExit>
                    <div>
                      <List component="div" disablePadding>
                        {filteredBoards.length > 0 ? (
                          filteredBoards.map((board) => {
                            return (
                              <Fragment key={board.id}>
                                {/* Board List Item */}
                                <Tooltip
                                  content={
                                    board.synced || parentBoard === board.id
                                      ? "Your subItem is already shared with this board."
                                      : ""
                                  }
                                >
                                  <ListItemButton
                                    onClick={() => {
                                      dispatch(setBoardId(Number(board?.id)));
                                      handleBoardClick(workspace.id, board.id);
                                    }}
                                    sx={{
                                      pl: 4,
                                      borderRadius: "4px", // Other styles here
                                    }}
                                    disabled={
                                      (board.synced ? true : false) || parentBoard === board.id
                                    }
                                  >
                                    <ListItemIcon
                                      sx={{ minWidth: "unset" }}
                                      className="!w-auto mr-1"
                                    >
                                      <Board />
                                    </ListItemIcon>
                                    <ListItemText
                                      primaryTypographyProps={{
                                        fontSize: "0.875rem",
                                        fontFamily: "Poppins",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                      }}
                                      primary={`${board.name}`}
                                    />
                                    {openBoards[workspace.id] &&
                                    openBoards[workspace.id][board.id] ? (
                                      <ExpandLess />
                                    ) : (
                                      <ExpandMore />
                                    )}
                                  </ListItemButton>
                                </Tooltip>

                                <Collapse
                                  in={
                                    openBoards[workspace.id] && openBoards[workspace.id][board.id]
                                  }
                                  timeout={100}
                                  unmountOnExit
                                >
                                  <div id="add_item_step_3_small">
                                    <List component="div" disablePadding>
                                      {/* Board Item */}
                                      {boardGroups[board.id] ? (
                                        boardGroups[board.id].map((group, index) => (
                                          <ListItemButton
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setPreview({
                                                workspaceName: workspace.name,
                                                board,
                                                group,
                                              });
                                            }}
                                            key={group.id}
                                            sx={{ pl: 6 }}
                                            disabled={
                                              board.synced ||
                                              isDisableAll ||
                                              itemsDisabled.some((item) =>
                                                item.endsWith(`-${board.id}`),
                                              )
                                            }
                                          >
                                            <ListItemIcon
                                              sx={{
                                                minWidth: "unset",
                                                color: `${group.color}`,
                                              }}
                                              className="!w-auto mr-1"
                                            >
                                              <Group />
                                            </ListItemIcon>
                                            <ListItemText
                                              primaryTypographyProps={{
                                                fontSize: "0.875rem",
                                                fontFamily: "Poppins",
                                                color: `${group.color}`,
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                              }}
                                              primary={group.title}
                                            />

                                            {itemsLoader.includes(`${group.id}-${board.id}`) && (
                                              <Spinner size={14} />
                                            )}
                                          </ListItemButton>
                                        ))
                                      ) : (
                                        <Spinner />
                                      )}
                                    </List>
                                  </div>
                                </Collapse>
                              </Fragment>
                            );
                          })
                        ) : (
                          <div>No boards match your search</div>
                        )}
                      </List>
                    </div>
                  </Collapse>
                </Fragment>
              );
            })}
          </List>
        </div>
      )}
    </>
  );
};

export default WorkspacesAndBoardsList;
